import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Rating,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import BedIcon from '@mui/icons-material/Bed';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { getHotelProductInfo } from '../../../api/endpoints/hotel';
import { SendForApprovalPanel } from '../../../components/booking/SendForApprovalPanel';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import type { HotelFlowState } from '../flowConfig';
import type { HotelBooking } from '../../../api/types/hotel';
import { AboutTab, offerBoardName, offerRoomName } from './ResultsStep';

function formatMoney(amount?: number, currency?: string): string {
  if (amount === undefined || amount === null) return '—';
  return `${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? 'TRY'}`;
}

function formatDate(iso?: string) {
  return iso ? iso.slice(0, 10).split('-').reverse().join('.') : '—';
}

/** Sağ panel — otel/oda bilgileri ve `HotelBooking`'in (`PricingBase`) alanlarından
 *  başlık başlık ödenecek tutar dökümü. `state.shoppingCart.remainingTotal` en güncel
 *  kalan tutar olduğu için toplamda o esas alınıyor, booking'in kendi `totalFare`'ı
 *  yalnızca fallback. */
function PaymentSummary({ flow }: { flow: ReturnType<typeof useBookingFlowContext<HotelFlowState>> }) {
  const { selectedOffer, searchRequest, shoppingCart } = flow.state;
  const booking = shoppingCart?.hotelBookings?.[0] as HotelBooking | undefined;
  const currency = booking?.currency ?? selectedOffer?.currencyType ?? 'TRY';

  const productInfoQuery = useQuery({
    queryKey: ['hotelProductInfo', selectedOffer?.hotelId, selectedOffer?.arkmanHotel],
    queryFn: () => getHotelProductInfo(selectedOffer?.hotelId as string, selectedOffer?.arkmanHotel ?? false),
    enabled: !!selectedOffer?.hotelId,
    staleTime: 5 * 60 * 1000,
  });

  const lines: { label: string; amount?: number }[] = [
    { label: 'Oda Bedeli', amount: booking?.baseFare },
    { label: 'Vergiler', amount: booking?.totalTax },
    { label: 'Hizmet Bedeli', amount: booking?.totalServiceFee },
    { label: 'Ekstra Ücret', amount: booking?.totalExtraFee },
    { label: 'Markup', amount: booking?.totalMarkupFare },
  ].filter((line) => !!line.amount);

  const total = shoppingCart?.remainingTotal ?? booking?.totalFare ?? selectedOffer?.servicePrice;

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Otel Bilgileri
          </Typography>
          {selectedOffer?.hotelImageUrl && (
            <Box
              component="img"
              src={selectedOffer.hotelImageUrl}
              alt={selectedOffer.hotelName}
              sx={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 1, mb: 1.5 }}
            />
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>
            {selectedOffer?.hotelName ?? booking?.hotelName ?? 'Otel'}
          </Typography>
          {selectedOffer?.star !== undefined && (
            <Rating value={selectedOffer.star} precision={0.5} size="small" readOnly sx={{ mt: 0.5 }} />
          )}
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 1 }}>
            <LocationOnIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {[selectedOffer?.cityName, selectedOffer?.countryName].filter(Boolean).join(', ') ||
                selectedOffer?.address ||
                '—'}
            </Typography>
          </Stack>

          {selectedOffer && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="subtitle2" gutterBottom>
                Otel Özellikleri
              </Typography>
              <AboutTab
                info={selectedOffer}
                productInfo={productInfoQuery.data}
                productInfoLoading={productInfoQuery.isFetching}
              />
            </>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="subtitle2" gutterBottom>
            Seçilen Oda
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <BedIcon fontSize="small" color="primary" />
            <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
              {(selectedOffer && offerRoomName(selectedOffer)) ?? booking?.roomName ?? '—'}
            </Typography>
          </Stack>
          {selectedOffer && offerBoardName(selectedOffer) && (
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mt: 0.5 }}>
              <RestaurantIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {offerBoardName(selectedOffer)}
              </Typography>
            </Stack>
          )}
          <Typography variant="body2" sx={{ mt: 1 }}>
            Giriş: {formatDate(booking?.startDate ?? searchRequest?.startDate)} · Çıkış:{' '}
            {formatDate(booking?.endDate ?? searchRequest?.endDate)}
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Ödenecek Tutar
          </Typography>
          <Stack divider={<Divider />} spacing={1}>
            {lines.map((line) => (
              <Stack key={line.label} direction="row" sx={{ justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  {line.label}
                </Typography>
                <Typography variant="body2">{formatMoney(line.amount, currency)}</Typography>
              </Stack>
            ))}
            <Stack direction="row" sx={{ justifyContent: 'space-between', pt: lines.length ? 0.5 : 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Toplam
              </Typography>
              <Typography variant="subtitle1" color="success.main" sx={{ fontWeight: 700 }}>
                {formatMoney(total, currency)}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
}

/** Kart alanları yalnızca `useCurrentPayment` false iken zorunlu — cari (running-account)
 *  ödeme kart bilgisi göndermiyor, air modülündeki `AirPaymentForm` ile aynı desen (bkz.
 *  api/types/hotel.ts'teki `PaymentRequest` başlığı). */
const schema = z
  .object({
    cardHolder: z.string().optional(),
    cardNumber: z.string().optional(),
    cvv: z.string().optional(),
    expirationMonth: z.string().optional(),
    expirationYear: z.string().optional(),
    installmentCount: z.string().optional(),
    useCurrentPayment: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.useCurrentPayment) return;
    if (!values.cardHolder) {
      ctx.addIssue({ code: 'custom', path: ['cardHolder'], message: 'Kart sahibi gerekli' });
    }
    if (!values.cardNumber || !/^\d{13,19}$/.test(values.cardNumber)) {
      ctx.addIssue({ code: 'custom', path: ['cardNumber'], message: 'Geçerli bir kart numarası girin' });
    }
    if (!values.cvv || !/^\d{3,4}$/.test(values.cvv)) {
      ctx.addIssue({ code: 'custom', path: ['cvv'], message: 'Geçerli bir CVV girin' });
    }
    if (!values.expirationMonth || !/^(0[1-9]|1[0-2])$/.test(values.expirationMonth)) {
      ctx.addIssue({ code: 'custom', path: ['expirationMonth'], message: 'AA formatında girin (örn. 09)' });
    }
    if (!values.expirationYear || !/^\d{2}$/.test(values.expirationYear)) {
      ctx.addIssue({ code: 'custom', path: ['expirationYear'], message: 'YY formatında girin (örn. 28)' });
    }
  });

type FormValues = z.infer<typeof schema>;

function formatBalance(limit?: number): string {
  if (limit === undefined || limit === null) return '—';
  return `${limit.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`;
}

export function PaymentStep() {
  const flow = useBookingFlowContext<HotelFlowState>();
  const { selectedCurrentAccount, shoppingCart, selectedOffer } = flow.state;
  const amount = shoppingCart?.remainingTotal ?? selectedOffer?.servicePrice;
  const currentPaymentInsufficient = !!selectedCurrentAccount && (selectedCurrentAccount.limit ?? 0) < (amount ?? 0);
  const currentPaymentAvailable = !!selectedCurrentAccount && !currentPaymentInsufficient;

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      cardHolder: '',
      cardNumber: '',
      cvv: '',
      expirationMonth: '',
      expirationYear: '',
      installmentCount: '1',
      useCurrentPayment: false,
    },
  });

  const useCurrentPayment = watch('useCurrentPayment');

  const onSubmit = async (values: FormValues) => {
    if (values.useCurrentPayment) {
      await flow.runStep({ paymentType: 'CurrentPayment', paymentForm: undefined });
      return;
    }
    await flow.runStep({
      paymentType: 'CreditCardPayment',
      paymentForm: {
        cardHolder: values.cardHolder ?? '',
        cardNumber: values.cardNumber ?? '',
        cvv: values.cvv ?? '',
        expirationMonth: values.expirationMonth ?? '',
        expirationYear: values.expirationYear ?? '',
        installmentCount: values.installmentCount ? Number(values.installmentCount) : 1,
        currency: 'TRY',
        returnUrl: window.location.href,
      },
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <SendForApprovalPanel product="Otel" cart={shoppingCart} />
      </Grid>
      <Grid size={{ xs: 12, md: 7 }}>
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Ödeme
            </Typography>
            {flow.error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {flow.error}
              </Alert>
            )}
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedCurrentAccount && (
                <Box>
                  <FormControlLabel
                    control={
                      <Controller
                        control={control}
                        name="useCurrentPayment"
                        render={({ field }) => (
                          <Checkbox
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                            disabled={!currentPaymentAvailable}
                          />
                        )}
                      />
                    }
                    label={`Cari ödeme (Bakiye: ${formatBalance(selectedCurrentAccount.limit)})`}
                  />
                  {currentPaymentInsufficient && (
                    <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                      Cari hesap bakiyesi ödenecek tutarı karşılamıyor — kredi limiti için lütfen ilgili birimle
                      irtibata geçin.
                    </Typography>
                  )}
                </Box>
              )}

              {!useCurrentPayment && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Kart Üzerindeki İsim"
                      size="small"
                      fullWidth
                      error={!!errors.cardHolder}
                      helperText={errors.cardHolder?.message}
                      {...register('cardHolder')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Kart Numarası"
                      size="small"
                      fullWidth
                      error={!!errors.cardNumber}
                      helperText={errors.cardNumber?.message}
                      {...register('cardNumber')}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="Ay (AA)"
                      size="small"
                      fullWidth
                      error={!!errors.expirationMonth}
                      helperText={errors.expirationMonth?.message}
                      {...register('expirationMonth')}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="Yıl (YY)"
                      size="small"
                      fullWidth
                      error={!!errors.expirationYear}
                      helperText={errors.expirationYear?.message}
                      {...register('expirationYear')}
                    />
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    <TextField
                      label="CVV"
                      size="small"
                      fullWidth
                      error={!!errors.cvv}
                      helperText={errors.cvv?.message}
                      {...register('cvv')}
                    />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <TextField label="Taksit Sayısı" type="number" size="small" fullWidth {...register('installmentCount')} />
                  </Grid>
                </Grid>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button type="submit" variant="contained" disabled={flow.status === 'running'}>
                  {flow.status === 'running'
                    ? 'Ödeme işleniyor…'
                    : useCurrentPayment
                      ? 'Cari ile Öde'
                      : 'Öde'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 5 }}>
        <PaymentSummary flow={flow} />
      </Grid>
    </Grid>
  );
}
